// Settings export and import (plus benchmarks and clear), in the shell's top bar. The v3 equivalents of
// v2's header actions. An exported file carries a v3 payload; reading it (or an older v2 one, or an
// old `?s=` share link, which GuidedApp still opens) goes through the migration, so nothing here
// knows about v2.
//
// Import replaces the current sizing: it hydrates the store, which re-anchors autosave, so loading
// a file is never counted as an edit.

import { useRef } from 'react';
import { buildSettingsMarkdown, settingsDateStr, settingsFilename, v3Payload } from '../../engine/sizing-transfer';
import { useCalculatorStore } from '../../state/calculator-store';
import { showNotice } from '../../state/notice-store';
import { applyLoaded, customerNameOf, loadFromConfig, sizingStateOf } from '../../state/load-paths';
import { Ph } from '../Icon';
import { BenchmarksButton } from './BenchmarksPanel';
import { ClearInputsButton } from './ClearInputsButton';

export function SessionTools() {
  const fileRef = useRef<HTMLInputElement>(null);
  function exportSettings() {
    const state = useCalculatorStore.getState();
    const name = customerNameOf(state.account);
    const md = buildSettingsMarkdown(v3Payload(sizingStateOf(state, name), state.recheck));
    const url = URL.createObjectURL(new Blob([md], { type: 'text/markdown' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = settingsFilename(name, settingsDateStr(new Date()));
    a.click();
    URL.revokeObjectURL(url);
  }

  async function importSettings(file: File) {
    let text: string;
    try {
      text = await file.text();
    } catch {
      return showNotice({ tone: 'error', text: 'That file could not be read.' });
    }
    const loaded = loadFromConfig(text, useCalculatorStore.getState().account);
    if (!loaded) return showNotice({ tone: 'error', text: 'That file is not a calculator settings export.' });
    applyLoaded(loaded);
    showNotice({
      tone: 'info',
      text: loaded.recheck.length
        ? `Settings imported. ${loaded.recheck.length} ${loaded.recheck.length === 1 ? 'answer needs' : 'answers need'} a re-check; they are flagged in the review.`
        : 'Settings imported.',
    });
  }

  return (
    <div className="vc3-tools-bar">
      <button type="button" className="vc3-linkbtn" onClick={exportSettings}>
        <Ph name="download-simple" size={13} /> Export
      </button>
      <button type="button" className="vc3-linkbtn" onClick={() => fileRef.current?.click()}>
        <Ph name="upload-simple" size={13} /> Import
      </button>
      <BenchmarksButton />
      <ClearInputsButton />
      <input
        ref={fileRef}
        type="file"
        accept=".md,text/markdown,text/plain"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void importSettings(file);
        }}
      />
    </div>
  );
}
